<template>
  <div
    class="wrapper"
    :class="{
      'wrapper-dark-theme': isTwitchUser,
      'wrapper--mobile-bottom-nav': showMobileBottomNav,
    }"
  >
    <side-bar :key="sidebarRenderKey" :nav-links="visibleSidebarLinks" />
    <div
      class="main-panel"
      :class="{
        'main-panel-dark-theme': isTwitchUser,
        'main-panel--create-clips-detail': isCreateClipsDetail,
      }"
    >
      <dashboard-content
        :class="{ 'content--create-clips-detail': isCreateClipsDetail }"
        @click.native="toggleSidebar"
      />
    </div>
    <mobile-bottom-nav v-if="showMobileBottomNav" />
  </div>
</template>
<style lang="scss"></style>
<script>
import { mapState } from "pinia";
import DashboardContent from "./Content.vue";
import MobileBottomNav from "@/components/MobileBottomNav.vue";
import { isImpersonating } from "@/utils/impersonation";
import {
  CREATE_CLIPS_MOBILE_WORKSPACE_CLOSE_EVENT,
  getNavRoutes,
} from "@/config/navRoutes";
import { useCurrentUserStore } from "@/stores/currentUser";

export default {
  components: {
    DashboardContent,
    MobileBottomNav,
  },
  watch: {
    "$sidebar.showSidebar"(isOpen) {
      if (!isOpen) return;
      window.dispatchEvent(new Event(CREATE_CLIPS_MOBILE_WORKSPACE_CLOSE_EVENT));
    },
  },
  computed: {
    ...mapState(useCurrentUserStore, ["hasAdminAccess", "isTwitchUser"]),
    sidebarRenderKey() {
      return [
        this.hasAdminAccess ? "admin" : "user",
        this.isTwitchUser ? "twitch" : "not-twitch",
        isImpersonating() ? "impersonating" : "normal",
      ].join("|");
    },
    visibleSidebarLinks() {
      const locale = this.$i18n?.locale;
      void locale;
      const routes = getNavRoutes(this.isTwitchUser);
      return routes.filter((link) => link.path !== "/studio");
    },
    isCreateClipsDetail() {
      return /^\/create-clips\/[^/]+$/.test(String(this.$route?.path || ""));
    },
    showMobileBottomNav() {
      return !this.isCreateClipsDetail;
    },
  },
  methods: {
    toggleSidebar() {
      if (this.$sidebar.showSidebar) {
        this.$sidebar.displaySidebar(false);
      }
    },
  },
};
</script>
<style scoped>
.wrapper-dark-theme .main-panel-dark-theme {
  background: var(--clipr-bg-login-right) !important;
  color: var(--clipr-text-primary);
}

.wrapper-dark-theme .main-panel-dark-theme > .content {
  background: transparent;
  color: var(--clipr-text-primary);
}

.wrapper :deep(.main-panel > .content) {
  padding-top: 30px;
}

.wrapper :deep(.main-panel > .content.content--create-clips-detail) {
  padding: 0;
}

.wrapper :deep(.main-panel > .content.content--create-clips-detail > .container-fluid) {
  padding: 0;
}

@media (max-width: 991px) {
  /*
   * In-flow mobile shell: bottom nav is a flex footer (not position:fixed).
   * Avoids transform/100vh trapping that hid the nav under Chrome UI and
   * let billing content scroll underneath the bar.
   */
  .wrapper.wrapper--mobile-bottom-nav {
    display: flex;
    flex-direction: column;
    height: 100dvh;
    max-height: 100dvh;
    min-height: 0;
    overflow: hidden;
  }

  .wrapper.wrapper--mobile-bottom-nav :deep(.main-panel) {
    float: none;
    width: 100%;
    flex: 1 1 auto;
    min-height: 0;
    height: auto;
    max-height: none;
    overflow-x: hidden;
    overflow-y: auto;
  }

  .wrapper--mobile-bottom-nav :deep(.main-panel > .content:not(.content--create-clips-detail)) {
    padding-top: 30px;
    /* Nav is in-flow now — no extra clearance needed for a fixed bar */
    padding-bottom: 24px;
  }

  .main-panel.main-panel--create-clips-detail {
    height: 100dvh;
    min-height: 0;
    max-height: 100dvh;
    background: transparent !important;
    overflow: hidden;
  }

  .wrapper :deep(.main-panel--create-clips-detail > .content.content--create-clips-detail),
  .wrapper :deep(.main-panel--create-clips-detail > .content.content--create-clips-detail > .container-fluid) {
    height: 100%;
    min-height: 0;
    overflow: hidden;
  }
}
</style>
